import { Search } from "lucide-react";
import { useEffect, useState, type FormEvent, type JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "@mailflare/ui";
import type { MailboxId } from "@mailflare/shared";

import { FilterChips, type SearchFilterState } from "./filter-chips";
import { buildSearchQuery } from "./params";

function filtersFromParams(params: URLSearchParams): SearchFilterState {
  return {
    isUnread: params.get("isUnread") === "true",
    hasAttachment: params.get("hasAttachment") === "true",
  };
}

/** Global search input with filter chips. Submits to the search route. */
export function SearchBar({ mailboxId }: { readonly mailboxId: MailboxId }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const isSearchRoute = location.pathname.endsWith("/search");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilterState>({
    isUnread: false,
    hasAttachment: false,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (isSearchRoute) {
      setQuery(params.get("q") ?? "");
      setFilters(filtersFromParams(params));
    }
  }, [isSearchRoute, location.search]);

  const go = (nextQuery: string, nextFilters: SearchFilterState): void => {
    const qs = buildSearchQuery({ q: nextQuery, ...nextFilters });
    navigate(`/mail/${mailboxId}/search${qs.length > 0 ? `?${qs}` : ""}`);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    go(query, filters);
  };

  const handleFilterChange = (next: SearchFilterState): void => {
    setFilters(next);
    if (isSearchRoute) go(query, next);
  };

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <form role="search" onSubmit={handleSubmit} className="relative w-full max-w-2xl">
        <Search
          aria-hidden
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2"
        />
        <Input
          id="global-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search mail"
          aria-label="Search mail"
          data-testid="search-input"
          className="pl-9"
        />
      </form>
      {isSearchRoute ? (
        <div className="hidden md:block">
          <FilterChips value={filters} onChange={handleFilterChange} />
        </div>
      ) : null}
    </div>
  );
}
